import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ChevronRight, Home } from "lucide-react";
import { useT } from "@/lib/i18n";
import { getRelated } from "@/lib/calculators";
import { getAliases } from "@/lib/seoAliases";
import { CalculatorCard } from "./CalculatorCard";
import { Icon } from "@/components/Icon";

interface Props {
  slug: string;
  title: string;
  description: string;
  iconName?: string;
  children: ReactNode;
  faq?: { q: string; a: string }[];
}

export function CalculatorLayout({ slug, title, description, iconName, children, faq }: Props) {
  const { t, lang } = useT();
  const related = getRelated(slug, 6);
  const aliases = getAliases(`/${slug}`);

  return (
    <div className="mx-auto max-w-4xl px-3 py-6 sm:px-4 sm:py-8 md:px-6 md:py-12">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-accent">
          <Home size={12} /> {t("common.home")}
        </Link>
        <ChevronRight size={12} />
        <span className={lang === "bn" ? "font-bn" : ""}>{title}</span>
      </nav>

      <div className="mb-6 flex items-start gap-4">
        {iconName && (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon name={iconName} size={24} />
          </span>
        )}
        <div>
          <h1 className={`text-2xl font-bold tracking-tight md:text-3xl ${lang === "bn" ? "font-bn" : ""}`}>{title}</h1>
          <p className={`mt-1 text-sm text-muted-foreground md:text-base ${lang === "bn" ? "font-bn" : ""}`}>
            {description}
          </p>
        </div>
      </div>

      <CalculatorCard>{children}</CalculatorCard>

      {aliases.length > 0 && (
        <section className="mt-6 rounded-xl border border-border bg-muted/30 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {lang === "bn" ? "এই টুলটি যে নামেও পরিচিত" : "Also known as"}
          </h2>
          <p className="mt-2 text-sm text-foreground/80">
            {aliases.map((a, i) => (
              <span key={a}>
                {i > 0 && <span className="text-muted-foreground"> · </span>}
                <span className="capitalize">{a}</span>
              </span>
            ))}
          </p>
        </section>
      )}

      {faq && faq.length > 0 && (
        <section className="mt-12">
          <h2 className={`mb-4 text-xl font-bold ${lang === "bn" ? "font-bn" : ""}`}>{t("common.faq")}</h2>
          <div className="divide-y divide-border rounded-xl border border-border bg-card">
            {faq.map((item, i) => (
              <details key={i} className="group p-4">
                <summary className={`cursor-pointer text-sm font-semibold ${lang === "bn" ? "font-bn" : ""}`}>{item.q}</summary>
                <p className={`mt-2 text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="mt-12">
        <h2 className={`mb-4 text-xl font-bold ${lang === "bn" ? "font-bn" : ""}`}>{t("common.related")}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((c) => (
            <Link
              key={c.slug}
              to="/$slug"
              params={{ slug: c.slug }}
              className="group rounded-xl border border-border bg-card p-4 transition hover:border-accent hover:shadow-md"
            >
              <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground/70 group-hover:bg-accent/10 group-hover:text-accent">
                <Icon name={c.icon} size={18} />
              </div>
              <h3 className={`text-sm font-semibold ${lang === "bn" ? "font-bn" : ""}`}>{c.name[lang]}</h3>
              <p className={`mt-1 line-clamp-2 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                {c.description[lang]}
              </p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
